'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import HighlightedText from '@/components/HighlightedText/HighlightedText';

// Helper function to render text with bulletpoints and highlights
const renderTextWithBulletpoints = (text: string) => {
  // Check if text contains [Bulletpoint] keyword
  if (text.includes('[Bulletpoint]')) {
    // Split by [Bulletpoint] and create bullet list
    const parts = text.split('[Bulletpoint]').filter((part) => part.trim());

    return (
      <ul className="list-disc list-inside space-y-2">
        {parts.map((part, index) => {
          const trimmedPart = part.trim();

          // Check if the PREVIOUS part ended with [indent] (meaning this bullet should be indented)
          const prevPart = index > 0 ? parts[index - 1] : '';
          const shouldIndent = prevPart.trim().endsWith('[indent]');

          // Clean the current part (remove any [indent] at the end for the next bullet)
          const cleanText = trimmedPart.replace(/\[indent\]$/, '').trim();

          return (
            <li
              key={index}
              className={`text-white/90 ${shouldIndent ? 'ml-6 list-[circle]' : ''}`}
            >
              <HighlightedText text={cleanText} />
            </li>
          );
        })}
      </ul>
    );
  }

  // If no bulletpoints, just return highlighted text
  return <HighlightedText text={text} />;
};

/**
 * Props for `CaseStudiesOurApproach`.
 */
export type CaseStudiesOurApproachProps =
  SliceComponentProps<Content.StepListWithQuoteSlice>;

/**
 * Component for "CaseStudiesOurApproach" Slices.
 */
const CaseStudiesOurApproach: FC<CaseStudiesOurApproachProps> = ({ slice }) => {
  const { intro, steps, quote, quote_attribution, optionaldescription } =
    slice.primary;
  const linearGreenToDark =
    'linear_green_to_dark' in slice.primary
      ? (slice.primary as { linear_green_to_dark?: boolean })
          .linear_green_to_dark
      : undefined;

  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in-from-right');
          entry.target.classList.remove('opacity-0', 'translate-x-12');
        }
      });
    }, observerOptions);

    // Capture current refs to use in cleanup
    const currentSteps = stepsRef.current;

    // Observe each step with a staggered delay
    currentSteps.forEach((stepEl, index) => {
      if (stepEl) {
        // Add initial hidden state
        stepEl.classList.add(
          'opacity-0',
          'translate-x-12',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        stepEl.style.transitionDelay = `${index * 150}ms`;
        observer.observe(stepEl);
      }
    });

    return () => {
      currentSteps.forEach((stepEl) => {
        if (stepEl) observer.unobserve(stepEl);
      });
    };
  }, [steps]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="text-white font-inter py-16"
      style={{
        background: linearGreenToDark
          ? 'linear-gradient(180deg, #0F3932 58.58%, #0E1110 100%)'
          : '#0F3932',
      }}
    >
      <div className="container mx-auto px-4 sm:px-10 lg:px-[174px]">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left side - Intro/Headline */}
          <div className="lg:w-1/3 flex-shrink-0">
            {intro && (
              <div className="text-white font-inter font-medium text-[40px] leading-[120%]">
                <HighlightedText text={asText(intro) || ''} />
              </div>
            )}
            {/* Optional description shown under the intro on the left side */}
            {optionaldescription && (
              <div className="text-white font-inter font-normal text-[18px] leading-[150%] mt-4">
                <PrismicRichText
                  field={optionaldescription}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="mb-0">{children}</p>
                    ),
                  }}
                />
              </div>
            )}
          </div>

          {/* Right side - Steps */}
          <div className="lg:w-2/3 flex flex-col gap-6">
            {steps && steps.length > 0 && (
              <>
                {steps.map((step, index) => (
                  <div
                    key={index}
                    ref={(el) => {
                      stepsRef.current[index] = el;
                    }}
                    className="rounded-2xl border border-[rgba(5,242,115,0.40)] bg-gradient-to-b from-[rgba(0,0,0,0.20)] to-[rgba(0,0,0,0.00)] shadow-[0_4.325px_64.871px_0_rgba(0,0,0,0.08)] backdrop-blur-[10px] p-6 cursor-default transform transition-transform duration-300 ease-out hover:translate-z-0 hover:scale-[1.02] hover:shadow-[0_8px_80px_0_rgba(0,0,0,0.15)]"
                  >
                    {/* Step Number and Title */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-[#05F273] font-inter font-bold text-lg">
                        {step.number}.
                      </span>
                      <h3 className="text-[#05F273] font-inter font-bold text-[24px] leading-[140%]">
                        {step.title}
                      </h3>
                    </div>

                    {/* Step Description */}
                    {step.description && (
                      <div className="font-inter font-normal text-[16px] leading-[150%] text-white/90">
                        {renderTextWithBulletpoints(
                          asText(step.description) || '',
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Quote Section - Outside flex, centered */}
        {quote && (
          <div className="mt-16 text-center">
            <div className="text-[#D7EDE3] font-inter font-medium text-[32px] leading-normal">
              <HighlightedText text={asText(quote) || ''} />
            </div>

            {quote_attribution && (
              <div className="text-[#D7EDE3] font-inter font-medium text-[32px] leading-normal mt-4">
                — {quote_attribution}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default CaseStudiesOurApproach;
